import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { gridSpacing } from 'store/constant';
import useAuth from 'hooks/useAuth';
import { Typography, CircularProgress, Box, Button, Container, Stack, Grid } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import SubCard from 'ui-component/cards/SubCard';
import { Country, getCountryFromlabel, getCountryFromCode } from 'utils/country';
export default function PublicCertificatePage() {
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [certData, setCertData] = useState<any>(null);
    const [error, setError] = useState('');
    const certId = searchParams.get('id');
    const navigate = useNavigate();
    const { user } = useAuth();

    useEffect(() => {
        if (certId) {
            axios
                .get(`https://verificodev.kimberleyprocess.com/v2/api/public/certificates/${certId}`)
                .then((res) => {
                    setCertData(res.data);
                    setLoading(false);
                })
                .catch(() => {
                    setError('Certificate not found.');
                    setLoading(false);
                });
        }
    }, [certId]);

    if (loading) return <CircularProgress />;
    if (error) return <Typography color="error">{error}</Typography>;

    return (
        <Container maxWidth="md" sx={{ mt: 10 }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                mb={4}
            >
                <Typography variant="h1" fontWeight={700}>
                    Public Certificate Details
                </Typography>
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<LoginIcon />}
                    onClick={() => navigate('/login')}
                    sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
                >
                    {Number(user?.role_id) === 4 ? 'All Certificate Details Below' : 'Login To View More Details'}
                </Button>
            </Stack>

            <Typography variant="h3" fontWeight={700} gutterBottom>
                Unique Serial ID : {certData?.usn}
            </Typography>
            {certData?.status === 'draft' ? (
                <Typography variant="body1" color="error" gutterBottom>
                    This certificate is in draft status and not yet finalized.
                </Typography>
            ) : (
                <Typography variant="body1" gutterBottom>
                    This certificate is finalized and valid.
                </Typography>
            )}
            <Typography variant="body1">
                This is an authenticated certificate issued by &nbsp;
                {getCountryFromCode(certData?.ic)?.label || 'relevant authority'}. If you have any questions, please contact the
                corresponding Verifico Office.
            </Typography>
            {Number(user?.role_id) === 4 ? (
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12} md={6}>
                        <SubCard title="Exporter">
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight={700} gutterBottom>
                                        Name:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" gutterBottom>
                                        {certData?.e_name || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight={700} gutterBottom>
                                        Address Line 1:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" gutterBottom>
                                        {certData?.e_addr1 || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight={700} gutterBottom>
                                        Address Line 2:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" gutterBottom>
                                        {certData?.e_addr2 || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight={700} gutterBottom>
                                        Town/City:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" gutterBottom>
                                        {certData?.e_city || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight={700} gutterBottom>
                                        State/Province:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" gutterBottom>
                                        {certData?.e_state || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight={700} gutterBottom>
                                        Zip/Postal Code:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" gutterBottom>
                                        {certData?.e_zip || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight={700} gutterBottom>
                                        Country:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" gutterBottom>
                                        <img
                                            loading="lazy"
                                            width="25"
                                            style={{ marginRight: 10, zIndex: 9 }}
                                            src={`https://flagcdn.com/w20/${getCountryFromCode(
                                                certData?.e_country
                                            )?.code.toLowerCase()}.png`}
                                            srcSet={`https://flagcdn.com/w40/${getCountryFromCode(
                                                certData?.e_country
                                            )?.code.toLowerCase()}.png 2x`}
                                            alt=""
                                        />
                                        {getCountryFromCode(certData?.e_country)?.label || 'relevant authority'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <SubCard title="Importer">
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight={700} gutterBottom>
                                        Name:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" gutterBottom>
                                        {certData?.i_name || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight={700} gutterBottom>
                                        Address Line 1:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" gutterBottom>
                                        {certData?.i_addr1 || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight={700} gutterBottom>
                                        Address Line 2:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" gutterBottom>
                                        {certData?.i_addr2 || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight={700} gutterBottom>
                                        Town/City:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" gutterBottom>
                                        {certData?.i_city || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight={700} gutterBottom>
                                        State/Province:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" gutterBottom>
                                        {certData?.i_state || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight={700} gutterBottom>
                                        Zip/Postal Code:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" gutterBottom>
                                        {certData?.i_zip || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight={700} gutterBottom>
                                        Country:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" gutterBottom>
                                        <img
                                            loading="lazy"
                                            width="25"
                                            style={{ marginRight: 10, zIndex: 9 }}
                                            src={`https://flagcdn.com/w20/${getCountryFromCode(
                                                certData?.i_country
                                            )?.code.toLowerCase()}.png`}
                                            srcSet={`https://flagcdn.com/w40/${getCountryFromCode(
                                                certData?.i_country
                                            )?.code.toLowerCase()}.png 2x`}
                                            alt=""
                                        />
                                        {getCountryFromCode(certData?.i_country)?.label || 'relevant authority'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                </Grid>
            ) : (
                <Typography variant="body1" gutterBottom></Typography>
            )}
        </Container>
    );
}
